import {DispatchAny} from '@pluto/@types';
import {
    createQCJobTimespanAdBreak,
    deleteQCJobTimespanAdBreak,
    getQCJobTimespanAdBreaks,
    TimespanAdBreak,
    updateQCJobTimespanAdBreak,
} from '@pluto/rest';
import {setNotificationAction} from '@pluto/notification';
import {createAction, Action, ActionFunction1, ActionFunction3} from 'redux-actions';
import tryToCatch from 'try-to-catch';

import {TimelineMarker, TimelineRowType} from 'VideoAudioQC/Timeline/TimelineTypes';
import {toggleSelectedMarkerAction} from 'VideoAudioQC/Timeline/Redux/TimelineActions';
import {curry} from 'ramda';
import {updateMarkerStart} from './AdBreaksUtils';

export const AD_BREAKS_SET = 'AD_BREAKS_SET';
export const AD_BREAKS_RESET = 'AD_BREAKS_RESET';
export const AD_BREAKS_ADD_MARKER = 'AD_BREAKS_ADD_MARKER';
export const AD_BREAKS_EDIT = 'AD_BREAKS_EDIT';
export const AD_BREAKS_DELETE = 'AD_BREAKS_DELETE';

export interface AddAdBreakMarkerPayload {
    marker: TimelineMarker;
}

export interface EditAdBreaksMarkerPayload {
    marker: TimelineMarker;
}

export type SetAdBreaksPayload = Promise<TimelineMarker[]>;

export const parseAdBreak = (adBreak: TimespanAdBreak): TimelineMarker => {
    const {
        id,
        startFrameNumber,
        durationFrames,
        createdByUser,
        updatedAt,
    } = adBreak;
    const start = startFrameNumber;
    const end = durationFrames ? startFrameNumber + durationFrames : startFrameNumber + 1;
    
    return {
        identifier: String(id),
        start,
        end,
        createdByUser,
        updatedAt,
    };
};

export const parseAdBreaks = (adBreaks: TimespanAdBreak[]): TimelineMarker[] => {
    if (!adBreaks)
        return [];
    
    return adBreaks.map(parseAdBreak);
};

export const setAdBreaksAction = createAction<SetAdBreaksPayload, string, number, number>(AD_BREAKS_SET, async (url: string, start: number, end: number): SetAdBreaksPayload => {
    const [error, adBreaks] = await tryToCatch(getQCJobTimespanAdBreaks, url, {
        start,
        end,
    });
    
    if (!error) {
        return parseAdBreaks(adBreaks);
    }
    
    console.error('Failed to fetch AdBreak markers.', error);
    
    return [];
});

export const resetAdBreaksAction = createAction<void>(AD_BREAKS_RESET);

export const addAdBreakMarkerAction = createAction(AD_BREAKS_ADD_MARKER, (marker: TimelineMarker): AddAdBreakMarkerPayload => {
    return {
        marker,
    };
});

export const createAdBreakMarkerAction = curry(async (url: string, duration: number, rowId: string, startFrameNumber: number, dispatch: DispatchAny) => {
    const updatedStart = updateMarkerStart(duration, startFrameNumber);
    
    const [error, adBreak] = await tryToCatch(createQCJobTimespanAdBreak, url, {
        startFrameNumber: updatedStart,
        durationFrames: 1,
    });
    
    if (!error) {
        const marker = parseAdBreak(adBreak);
        
        dispatch(addAdBreakMarkerAction(marker));
        dispatch(toggleSelectedMarkerAction(marker.identifier, rowId, TimelineRowType.AdBreak));
        
        return;
    }
    
    dispatch(setNotificationAction({
        type: 'error',
        text: 'Failed to create Ad Breakpoint.',
    }));
    
    console.error('Failed to create Ad Breakpoint.', error);
});

export const editAdBreaksMarkerAction = createAction<EditAdBreaksMarkerPayload, TimelineMarker>(AD_BREAKS_EDIT, (marker: TimelineMarker): EditAdBreaksMarkerPayload => {
    return {
        marker,
    };
});

export const updateAdBreaksMarkerAction = curry(async (url: string, duration: number, marker: TimelineMarker, dispatch: DispatchAny) => {
    const {
        start,
        identifier,
    } = marker;
    const updatedStart = updateMarkerStart(duration, start);
    const updatedMarker: TimelineMarker = {
        identifier,
        start: updatedStart,
        end: updatedStart + 1,
    };
    
    dispatch(editAdBreaksMarkerAction(updatedMarker));
    
    const markerUrl = `${url}/${identifier}`;
    const [error, savedMarker] = await tryToCatch(updateQCJobTimespanAdBreak, markerUrl, {
        startFrameNumber: updatedStart,
        durationFrames: 1,
    });
    
    if (error) {
        dispatch(setNotificationAction({
            type: 'error',
            text: 'Failed to save Ad Breakpoint.',
        }));
        
        console.error('Failed to save Ad Breakpoint.', error);
        
        return;
    }
    
    const {updatedAt} = savedMarker;
    dispatch(editAdBreaksMarkerAction({
        ...updatedMarker,
        updatedAt,
    }));
});

export const deleteAdBreaksMarkerAction = createAction<string>(AD_BREAKS_DELETE);

export const removeAdBreaksMarkerAction = curry(async (url: string, markerId: string, dispatch: DispatchAny) => {
    dispatch(deleteAdBreaksMarkerAction(markerId));
    
    const markerUrl = `${url}/${markerId}`;
    const [error] = await tryToCatch(deleteQCJobTimespanAdBreak, markerUrl);
    
    if (error) {
        dispatch(setNotificationAction({
            type: 'error',
            text: 'Failed to delete Ad Breakpoint.',
        }));
        
        console.error('Failed to delete Ad Breakpoint.', error);
    }
});

export type SetAdBreaksAction = ActionFunction3<string, number, number, Action<SetAdBreaksPayload>>;
export type ResetAdBreaksAction = ActionFunction1<void, Action<void>>;

export type CreateAdBreakMarkerAction = (url: string, duration: number, rowId: string, start: number) => Promise<void>;
export type UpdateAdBreaksMarkerAction = (url: string, duration: number, marker: TimelineMarker) => Promise<void>;
export type RemoveAdBreaksMarkerAction = (url: string, markerId: string) => Promise<void>;
